import { CLOTHING_DETAILS, STYLE_GUIDE } from './clothingDesignHelper'
import { MATERIAL_HIERARCHY, requiresUpgrade } from './materialHierarchy'

export const HANDOFF_KEY = 'style_ai_design_handoff'

/** In-memory cache so React Strict Mode double-mount doesn't drop handoff */
let handoffCache = null

export function saveDesignHandoff(payload) {
  try {
    handoffCache = { ...payload, timestamp: Date.now() }
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(handoffCache))
  } catch (_) {}
}

/** @deprecated use saveDesignHandoff */
export const saveStudioHandoff = saveDesignHandoff

export function peekDesignHandoff() {
  if (handoffCache) return handoffCache
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY)
    if (!raw) return null
    handoffCache = JSON.parse(raw)
    return handoffCache
  } catch {
    return null
  }
}

export function clearDesignHandoff() {
  handoffCache = null
  try {
    sessionStorage.removeItem(HANDOFF_KEY)
  } catch (_) {}
}

/** @deprecated use peekDesignHandoff + clearDesignHandoff */
export function consumeStudioHandoff() {
  const data = peekDesignHandoff()
  if (data) clearDesignHandoff()
  return data
}

const STYLE_ALIASES = {
  minimal: 'Minimalist',
  casual: 'Casual',
  formal: 'Formal',
  vintage: 'Vintage',
  contemporary: 'Contemporary',
  streetwear: 'Streetwear',
  athletic: 'Athletic',
  bohemian: 'Bohemian'
}

export function titleCase(value) {
  if (!value) return ''
  return value
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

export function normalizeGarmentType(value) {
  if (!value) return ''
  const match = Object.keys(CLOTHING_DETAILS).find((k) => k.toLowerCase() === value.toLowerCase())
  return match || titleCase(value)
}

export function normalizeMaterial(raw) {
  if (!raw) return ''
  const titled = titleCase(raw.trim())
  if (MATERIAL_HIERARCHY[titled]) return titled
  const key = Object.keys(MATERIAL_HIERARCHY).find(
    (k) => k.toLowerCase() === raw.toLowerCase() || titled.toLowerCase().includes(k.toLowerCase())
  )
  return key || titled
}

export function pickMaterialVariant(material, quality = 'premium') {
  if (!material || !MATERIAL_HIERARCHY[material]) return ''
  const variants = Object.keys(MATERIAL_HIERARCHY[material].variants)
  return variants.find((v) => !requiresUpgrade(material, v, quality)) || variants[0] || ''
}

export function normalizeStyle(raw) {
  if (!raw) return { style: '', extraNote: '' }
  const direct = Object.keys(STYLE_GUIDE).find((k) => k.toLowerCase() === raw.toLowerCase())
  if (direct) return { style: direct, extraNote: '' }
  const alias = STYLE_ALIASES[raw.toLowerCase()]
  if (alias) return { style: alias, extraNote: '' }
  return { style: '', extraNote: raw }
}

export function hasUrlTransferParams(searchParams) {
  return !!(
    searchParams.get('type') ||
    searchParams.get('material') ||
    searchParams.get('color') ||
    searchParams.get('style') ||
    searchParams.get('notes') ||
    searchParams.get('transfer')
  )
}

export function buildDesignQuery(specifications = {}, customInstructions = '') {
  const params = new URLSearchParams()
  params.set('transfer', '1')
  params.set('_h', String(Date.now()))
  if (specifications.type) params.set('type', specifications.type)
  if (specifications.material) params.set('material', specifications.material)
  if (specifications.color) params.set('color', specifications.color)
  if (specifications.style) params.set('style', specifications.style)
  if (customInstructions) params.set('notes', customInstructions)
  return `/design?${params.toString()}`
}

/**
 * Build normalized design session from handoff payload or URL search params.
 */
export function resolveDesignTransfer({ handoff = null, searchParams = null } = {}) {
  const quality = 'premium'

  if (handoff) {
    const specs = handoff.specifications || {}
    const material = normalizeMaterial(specs.material || '') || (specs.type ? 'Cotton' : '')
    const { style, extraNote } = normalizeStyle(specs.style || '')
    const notes = [handoff.customInstructions, specs.notes, extraNote]
      .filter(Boolean)
      .join('\n')

    return {
      source: handoff.source || 'studio',
      designData: {
        type: normalizeGarmentType(specs.type || ''),
        material,
        materialVariant: pickMaterialVariant(material, specs.quality || quality),
        quality: specs.quality || quality,
        style,
        color: specs.color ? titleCase(specs.color) : '',
        fit: '',
        neckline: '',
        sleeveLength: '',
        additionalNotes: ''
      },
      customInstructions: notes,
      generatedDesign: handoff.generatedDesign || null,
      currentStep: handoff.generatedDesign ? 3 : 1,
      presentationMode: specs.presentationMode === 'on_model' ? 'on_model' : 'product',
      modelGender: specs.modelGender === 'man' ? 'man' : 'woman'
    }
  }

  if (searchParams && hasUrlTransferParams(searchParams)) {
    const type = searchParams.get('type')
    const material = normalizeMaterial(searchParams.get('material') || '')
    const resolvedMaterial = material || (type ? 'Cotton' : '')
    const { style, extraNote } = normalizeStyle(searchParams.get('style') || '')
    const urlNotes = searchParams.get('notes') || ''
    const notes = [urlNotes, extraNote].filter(Boolean).join('\n')

    return {
      source: 'assistant',
      designData: {
        type: normalizeGarmentType(type || ''),
        material: resolvedMaterial,
        materialVariant: pickMaterialVariant(resolvedMaterial, quality),
        quality,
        style,
        color: searchParams.get('color') ? titleCase(searchParams.get('color')) : '',
        fit: '',
        neckline: '',
        sleeveLength: '',
        additionalNotes: ''
      },
      customInstructions: notes,
      generatedDesign: null,
      currentStep: 1,
      presentationMode: 'product',
      modelGender: 'woman'
    }
  }

  return null
}
